import React, { useEffect } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import {
  checkInputErrorMaxLength,
  checkInputErrorRequired,
  useDatePicker,
  useInput,
  useSelect,
  useViewport
} from '@chilecompra/react-kit'
import { DatePicker, Input, Select, Typography } from '@chilecompra/react-kit/components'
import { RESOLUTION_LABELS, USER_TYPE } from '../../config/settings/constant'
import { onGetAttachmentsTypesThunk, onGetSantionsTypesThunk, onUpdateFormValue } from './Sanction.actions'
import TextArea from '../../components/Inputs/TextArea'
import { Box, Column, Form, Row } from './SanctionEntryForm.styles'
import useReCaptcha from '../../modules/hooks/reCaptcha'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'

/**
 * The Saction Entry Form's container.
 */
const SanctionEntryForm = () => {
  const { userType } = useAuthorization()
  const { executeReCaptcha } = useReCaptcha()
  const dispatch = useDispatch()
  const {
    size: { isTiny, isUpToMedium }
  } = useViewport()
  const {
    attachmentsTypes: { data: attachmentsTypes },
    form: { attachmentType, backupDate, reason, resolutionNumber, sanctionType },
    poBiddingInfo: { error: poBiddingError, loading: poBiddingLoading },
    sanctionsTypes: { data: sanctionsTypes },
    userInfo: { error: userInfoError }
  } = useSelector(state => state.sanction)
  const { error: sellerError } = useSelector(state => state.basicSellerInformation)

  const {
    error: sanctionTypeError,
    value: sanctionTypeValue,
    onChange: handleSanctionTypeChange
  } = useSelect({
    initialValue: sanctionType,
    changeCallback: updatedValue => {
      dispatch(onUpdateFormValue({ name: 'sanctionType', value: updatedValue }))
    },
    overwriteCallback: () => sanctionType
  })
  const {
    error: reasonError,
    value: reasonValue,
    onChange: handleReasonChange
  } = useInput({
    initialValue: reason,
    errorCallbacks: [checkInputErrorRequired(), checkInputErrorMaxLength(1500)],
    changeCallback: updatedValue => {
      dispatch(onUpdateFormValue({ name: 'reason', value: updatedValue }))
    },
    overwriteCallback: () => reason
  })
  const {
    error: attachmentTypeError,
    value: attachmentTypeValue,
    onChange: handleAttachmentTypeChange
  } = useSelect({
    initialValue: attachmentType,
    changeCallback: updatedValue => {
      dispatch(onUpdateFormValue({ name: 'attachmentType', value: updatedValue }))
    },
    overwriteCallback: () => attachmentType
  })
  const {
    error: resolutionNumberError,
    value: resolutionNumberValue,
    onChange: handleResolutionNumberChange
  } = useInput({
    initialValue: resolutionNumber,
    errorCallbacks: [checkInputErrorRequired(), checkInputErrorMaxLength(40)],
    changeCallback: updatedValue => {
      dispatch(onUpdateFormValue({ name: 'resolutionNumber', value: updatedValue }))
    },
    overwriteCallback: () => resolutionNumber
  })
  const {
    error: backupDateError,
    value: backupDateValue,
    onChange: handleBackupDateChange
  } = useDatePicker({
    initialValue: backupDate,
    errorCallbacks: [checkInputErrorRequired()],
    changeCallback: updatedValue => {
      dispatch(onUpdateFormValue({ name: 'backupDate', value: updatedValue }))
    },
    overwriteCallback: () => backupDate
  })

  useEffect(() => {
    if (userType === '') return
    if (!poBiddingError && !poBiddingLoading) {
      batch(() => {
        dispatch(onGetSantionsTypesThunk())
        if (userType === USER_TYPE.PUBLIC) {
          const executeGetAttachmentsTypesThunk = async () => {
            await executeReCaptcha({ action: 'publicInfo' })
            dispatch(onGetAttachmentsTypesThunk())
          }

          executeGetAttachmentsTypesThunk()
        } else {
          dispatch(onGetAttachmentsTypesThunk())
        }
      })
    }
  }, [poBiddingError, poBiddingLoading])

  return userInfoError || poBiddingError || poBiddingLoading || sellerError ? null : (
    <Row margin="0">
      <Column xs={12} sm={12} md={9} padding={isUpToMedium ? '0 27px 0 0' : '0'}>
        <Form>
          <Box width={isTiny ? '100%' : '320px'}>
            <Select
              error={sanctionTypeError}
              label="Tipo de sanción"
              options={sanctionsTypes || []}
              value={sanctionTypeValue}
              onChange={handleSanctionTypeChange}
            />
          </Box>

          <TextArea
            error={reasonError}
            label="Motivo de sanción"
            maxCharactersForCounter={1500}
            maxLength={1500}
            rows={5}
            showCounter
            value={reasonValue}
            width="100%"
            onChange={handleReasonChange}
          />

          <Typography fontWeight="bold" variant="h4">
            Acto administrativo que aplica sanción
          </Typography>

          <Row margin="0">
            <Column xs={6} sm={6} md={4} padding="0 5px 0 0">
              <Select
                error={attachmentTypeError}
                label="Tipo de documento"
                options={attachmentsTypes || []}
                value={attachmentTypeValue}
                width="100%"
                onChange={handleAttachmentTypeChange}
              />
            </Column>
            <Column xs={6} sm={6} md={4} padding={isUpToMedium ? '0 5px 0 0' : '0'}>
              <Input
                error={resolutionNumberError}
                label={RESOLUTION_LABELS[attachmentType]}
                maxCharactersForCounter={40}
                maxLength={40}
                showCounter
                value={resolutionNumberValue}
                width="100%"
                onChange={handleResolutionNumberChange}
              />
            </Column>
            <Column xs={7} sm={6} md={4} padding="0">
              <DatePicker
                disableFuture
                error={backupDateError}
                label="Fecha documento de respaldo"
                value={backupDateValue}
                width="100%"
                onChange={handleBackupDateChange}
              />
            </Column>
          </Row>
        </Form>
      </Column>
    </Row>
  )
}

export default SanctionEntryForm
